import React, { useMemo } from 'react';
import {
  Badge,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { isSameDay, startOfMonth, subDays } from 'date-fns';
import { NominationFilters as NominationFiltersModel, NominatorOption } from '../nomination.types';
import { EMPTY_NOMINATION_FILTERS, countActiveFilters } from '../utils/nominationFilters';

export interface NominationFiltersProps {
  filters: NominationFiltersModel;
  onChange: (next: NominationFiltersModel) => void;
  nominatorOptions: NominatorOption[];
  locationOptions: string[];
  /** Admins see all locations and get a location filter; managers are already scoped. */
  showLocationFilter: boolean;
  resultCount: number;
  totalCount: number;
}

interface DatePreset {
  key: string;
  label: string;
  range: () => { from: Date; to: Date };
}

const DATE_PRESETS: DatePreset[] = [
  { key: '7d', label: 'Last 7 days', range: () => ({ from: subDays(new Date(), 6), to: new Date() }) },
  { key: '30d', label: 'Last 30 days', range: () => ({ from: subDays(new Date(), 29), to: new Date() }) },
  { key: 'month', label: 'This month', range: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
];

/** Filter bar for the Success Nominations table: search, nominator, date range, optional location. */
export const NominationFilters: React.FC<NominationFiltersProps> = ({
  filters,
  onChange,
  nominatorOptions,
  locationOptions,
  showLocationFilter,
  resultCount,
  totalCount,
}) => {
  const activeCount = countActiveFilters(filters);

  const activePresetKey = useMemo(() => {
    if (!filters.dateFrom || !filters.dateTo) return null;
    const match = DATE_PRESETS.find(preset => {
      const { from, to } = preset.range();
      return isSameDay(from, filters.dateFrom as Date) && isSameDay(to, filters.dateTo as Date);
    });
    return match?.key ?? null;
  }, [filters.dateFrom, filters.dateTo]);

  const update = (patch: Partial<NominationFiltersModel>) => onChange({ ...filters, ...patch });

  const applyPreset = (preset: DatePreset) => {
    if (activePresetKey === preset.key) {
      update({ dateFrom: null, dateTo: null });
      return;
    }
    const { from, to } = preset.range();
    update({ dateFrom: from, dateTo: to });
  };

  const summary =
    totalCount === 0
      ? 'No nominations in scope yet'
      : activeCount > 0
      ? `${resultCount} of ${totalCount} nominations match your filters`
      : `${totalCount} ${totalCount === 1 ? 'nomination' : 'nominations'}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={showLocationFilter ? 3 : 4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search client name, ID or staff"
              value={filters.search}
              onChange={event => update({ search: event.target.value })}
              inputProps={{ 'aria-label': 'Search nominations' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={showLocationFilter ? 2 : 3}>
            <FormControl fullWidth size="small">
              <InputLabel id="nominated-by-filter-label">Nominated by</InputLabel>
              <Select
                labelId="nominated-by-filter-label"
                label="Nominated by"
                value={filters.nominatorUid}
                onChange={event => update({ nominatorUid: event.target.value })}
              >
                <MenuItem value="">All staff</MenuItem>
                {nominatorOptions.map(option => (
                  <MenuItem key={option.uid} value={option.uid}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {showLocationFilter && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="location-filter-label">Location</InputLabel>
                <Select
                  labelId="location-filter-label"
                  label="Location"
                  value={filters.location}
                  onChange={event => update({ location: event.target.value })}
                >
                  <MenuItem value="">All locations</MenuItem>
                  {locationOptions.map(location => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={6} sm={3} md={2}>
            <DatePicker
              label="From"
              value={filters.dateFrom}
              onChange={value => update({ dateFrom: value })}
              maxDate={filters.dateTo ?? undefined}
              format="dd/MM/yyyy"
              slotProps={{
                textField: { size: 'small', fullWidth: true },
                field: { clearable: true, onClear: () => update({ dateFrom: null }) },
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <DatePicker
              label="To"
              value={filters.dateTo}
              onChange={value => update({ dateTo: value })}
              minDate={filters.dateFrom ?? undefined}
              disableFuture
              format="dd/MM/yyyy"
              slotProps={{
                textField: { size: 'small', fullWidth: true },
                field: { clearable: true, onClear: () => update({ dateTo: null }) },
              }}
            />
          </Grid>
          <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Tooltip title={activeCount > 0 ? 'Clear all filters' : 'No active filters'}>
              <span>
                <Badge badgeContent={activeCount} color="primary">
                  <IconButton
                    aria-label="Clear filters"
                    color="primary"
                    disabled={activeCount === 0}
                    onClick={() => onChange({ ...EMPTY_NOMINATION_FILTERS })}
                  >
                    <ClearIcon />
                  </IconButton>
                </Badge>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {DATE_PRESETS.map(preset => (
              <Chip
                key={preset.key}
                label={preset.label}
                size="small"
                clickable
                color={activePresetKey === preset.key ? 'primary' : 'default'}
                variant={activePresetKey === preset.key ? 'filled' : 'outlined'}
                onClick={() => applyPreset(preset)}
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {summary}
          </Typography>
        </Stack>
      </Paper>
    </LocalizationProvider>
  );
};

export default NominationFilters;
